// =============================================================================
// Add / edit member (§5.5, §7).
//
// §5.5 is a three-step flow: create the member, record the one-off fee, then
// they are eligible. Creating a member therefore lands straight on their detail
// screen, where the "Record membership fee" action is the obvious next thing —
// rather than returning to a list, which loses the thread.
//
// member_code is NOT a field here. It is generated by a database trigger (§4),
// and letting staff type one would eventually produce a duplicate.
// =============================================================================

import { el, mount } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { spinner } from '../lib/loading.js';
import { getMember, createMember, updateMember, listBranches } from '../lib/api.js';
import { getStaff } from '../lib/auth.js';

export function memberFormScreen(root, memberId = null) {
  const editing = memberId && memberId !== 'new';
  let busy = false;
  let branches = [];

  const view = el('div', { class: 'screen' });
  mount(root, view);

  const f = {
    full_name: input({ required: true, label: T.memberName, enterkeyhint: 'next' }),
    date_of_birth: input({ type: 'date', label: T.dateOfBirth }),
    guardian_name: input({ label: T.guardianName }),
    guardian_phone: input({ type: 'tel', label: T.guardianPhone, inputmode: 'tel' }),
    guardian_phone_2: input({ type: 'tel', label: T.guardianPhone2, inputmode: 'tel' }),
    guardian_email: input({ type: 'email', label: T.guardianEmail, inputmode: 'email' }),
    notes: el('textarea', { class: 'text-input', rows: '3', id: 'f-notes' }),
    branch: el('select', { class: 'text-input', id: 'f-branch' }),
  };

  // Every control gets a real id so its label can point at it with `for`.
  // Without that, tapping "Guardian phone" did nothing — on a phone that is a
  // 44px target thrown away on every field — and the name was announced twice,
  // once as the label's loose text and once as the aria-label.
  // A function declaration, not a const arrow: `f` above is built by calling
  // input(), which happens before this line runs, and a const would still be
  // in its temporal dead zone at that moment.
  function fieldId(label) {
    return 'f-' + label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  function input({ type = 'text', label, ...rest }) {
    return el('input', {
      // No placeholder. field() already renders a .field-label with this exact
      // string above the box, so every field on this form said its own name
      // twice — grey inside, dark above.
      type, class: 'text-input', id: fieldId(label),
      autocomplete: 'off', ...rest,
    });
  }

  // Front desk staff belong to one branch, so there is nothing to choose.
  // An admin has no home branch (§4) and must say which branch the child joins.
  const needsBranch = () => getStaff()?.role === 'admin';

  async function boot() {
    mount(view, spinner());
    try {
      if (needsBranch()) {
        branches = await listBranches();
        mount(f.branch, ...branches.map((b) => el('option', { value: b.id }, b.name)));
      }
      if (editing) {
        const m = await getMember(memberId);
        if (!m) { mount(view, el('p', { class: 'error' }, T.memberNotFound)); return; }
        f.full_name.value = m.full_name ?? '';
        f.date_of_birth.value = (m.date_of_birth ?? '').slice(0, 10);
        f.guardian_name.value = m.guardian_name ?? '';
        f.guardian_phone.value = m.guardian_phone ?? '';
        f.guardian_phone_2.value = m.guardian_phone_2 ?? '';
        f.guardian_email.value = m.guardian_email ?? '';
        f.notes.value = m.notes ?? '';
        if (needsBranch() && m.branch_id) f.branch.value = m.branch_id;
      }
      render();
    } catch (err) {
      mount(view, el('p', { class: 'error' }, err.message));
    }
  }

  function render() {
    mount(view,
      el('h1', { class: 'screen-title' }, editing ? T.editMember : T.newMember),
      el('form', { class: 'card', onSubmit: submit },
        field(T.memberName, f.full_name, true),
        field(T.dateOfBirth, f.date_of_birth, false, T.dobHint),
        needsBranch() ? field(T.branch, f.branch, true) : null,
        el('h2', { class: 'card-title card-section' }, T.guardian),
        field(T.guardianName, f.guardian_name),
        field(T.guardianPhone, f.guardian_phone, false, T.phoneHint),
        field(T.guardianPhone2, f.guardian_phone_2),
        field(T.guardianEmail, f.guardian_email),
        field(T.notes, f.notes),
        el('div', { class: 'form-actions' },
          el('button', {
            class: 'btn btn-primary', type: 'submit', disabled: busy,
          }, busy ? T.saving : editing ? T.saveChanges : T.createMember),
          el('button', {
            class: 'btn',
            onClick: () => { location.hash = editing ? `#/members/${memberId}` : '#/members'; },
          }, T.cancel),
        ),
      ),
    );
    if (!editing) queueMicrotask(() => f.full_name.focus());
  }

  function field(label, control, required = false, hint = null) {
    return el('div', { class: 'field' },
      el('label', { class: 'field-label', for: control.id || null },
        label, required ? el('span', { class: 'req' }, ' *') : null),
      control,
      hint ? el('p', { class: 'muted' }, hint) : null,
    );
  }

  function flash(message) {
    const note = el('p', { class: 'error', role: 'alert' }, message);
    view.querySelector('.card')?.before(note);
    setTimeout(() => note.remove(), 6000);
  }

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    const name = f.full_name.value.trim();
    if (!name) { f.full_name.focus(); return; }

    const fields = {
      full_name: name,
      // Empty date/email/phone inputs give '' — the columns are nullable, and
      // '' would be stored as a real value that later reads as "has an email".
      date_of_birth: f.date_of_birth.value || null,
      guardian_name: f.guardian_name.value.trim() || null,
      guardian_phone: f.guardian_phone.value.trim() || null,
      guardian_phone_2: f.guardian_phone_2.value.trim() || null,
      guardian_email: f.guardian_email.value.trim() || null,
      notes: f.notes.value.trim() || null,
    };
    if (needsBranch()) fields.branch_id = f.branch.value;

    busy = true; render();
    try {
      if (editing) {
        await updateMember(memberId, fields);
        location.hash = `#/members/${memberId}`;
      } else {
        const created = await createMember(fields);
        // Straight to the detail screen with the fee form ALREADY OPEN.
        //
        // The fee is not a field on this form. §5.5 is create, then charge, and
        // the database enforces that order — a payment needs a member id, and
        // the membership amount comes from branch settings, not from anything
        // typed here. Putting the fields on this form would mean one submit
        // that can half-succeed: a member created and a payment that failed,
        // with nothing on screen saying which. So it stays two writes, and the
        // second one is opened for you instead of waiting behind a button.
        location.hash = `#/members/${created.id ?? created.member_id}/pay`;
      }
    } catch (err) {
      busy = false; render();
      flash(err.message);
    }
  }

  boot();
  return { destroy: () => {} };
}
